import { DEFAULT_PAGE_SIZE } from "@/shared/constants";
import { QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import { flatten } from "lodash";
import { useMemo } from "react";
import postsApi from "@/api/posts/client";
import nftsApi from "@/api/nfts/client";
import { FeedPostDto, FeedType } from "@/api/posts/types";
export interface Props {
  queryKey: QueryKey;
  queryFn: any;
}

const useInfiniteFeedPosts = (feedType: FeedType) => {
  const { queryKey, queryFn } = useMemo(() => {
    switch (feedType) {
      case FeedType.GLOBAL:
        return {
          queryFn: postsApi.getGlobalFeedPosts,
          queryKey: ["global-feed"],
        };
      case FeedType.NEWS:
        return {
          queryFn: postsApi.getNewsFeedPosts,
          queryKey: ["news-feed"],
        };
      case FeedType.MY:
        return {
          queryFn: postsApi.getMyFeedPosts,
          queryKey: ["my-feed"],
        };
    }
  }, [feedType]);

  const { data, hasNextPage, fetchNextPage } = useInfiniteQuery(
    queryKey,
    async ({ pageParam = 0 }) => {
      const posts = await queryFn(
        DEFAULT_PAGE_SIZE,
        pageParam * DEFAULT_PAGE_SIZE,
      );

      if (feedType === FeedType.NEWS) {
        return await postsApi.enrichWithOgImage(posts);
      }

      const ogPosts = await postsApi.enrichWithOgMetadata(posts);

      const enrichedPosts = await nftsApi.enrichWithNft<FeedPostDto, "nft">(
        ogPosts,
        {
          attachedNftId: {
            name: "attachedNft",
            addressGetter: (item) => item.attachedNftId!,
          },
        },
      );

      return enrichedPosts;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < DEFAULT_PAGE_SIZE) {
          return undefined;
        }

        return allPages.length;
      },
    },
  );

  const posts = useMemo(() => {
    return data ? flatten(data.pages) : [];
  }, [data]);

  return { posts, hasNextPage, fetchNextPage };
};

export default useInfiniteFeedPosts;
